import { ref, watch } from "vue";
import { RouteMeta, RouteRecordName, RouteRecordRaw, RouteRecordRedirectOption } from "vue-router";

import type { Ref } from "vue";

import type { UserStore } from "@/stores/user";
import type { AccountStore } from "@/stores/account";

import { syncRouters } from "@/routers";
import { useUserStore } from "@/stores/user";
import { useAccountStore } from "@/stores/account";

export interface MenuOptions {
    path: string;
    name?: RouteRecordName;
    component?: RouteRecordRaw["component"];
    redirect?: RouteRecordRedirectOption;
    meta?: MetaProps;
    children?: MenuOptions[];
}

export interface MetaProps extends RouteMeta {
    icon?: string;
    title?: string;
    activeMenu?: RouteRecordName;
    isLink?: string;
    hidden?: boolean | ((props: { accountStore: AccountStore; userStore: UserStore }) => boolean);
    isFull?: boolean;
    isAffix?: boolean;
    isKeepAlive?: boolean;
    level?: number;
    authOptions?: { label: string; value: string }[];
}
//  未完成个人认证和已完成个人认证未完成组织机构认证只展示一个一级菜单：账号中心
let baseRoleRouteName = ["Me"];
// 递归获取子路由
const getBaseRouteNamesRecursive = (routes: RouteRecordRaw[]): string[] => {
    const names: string[] = [];

    routes.forEach(route => {
        const routeName = route.name as string;

        // 如果当前路由匹配 baseRoleRouteName，则递归收集其子路由 name
        if (baseRoleRouteName.includes(routeName)) {
            if (route.children?.length) {
                names.push(...route.children.map(child => child.name as string));
                names.push(...getBaseRouteNamesRecursive(route.children)); // 继续递归
            }
        }

        // 检查子路由是否包含 baseRoleRouteName 的值
        if (route.children?.length) {
            names.push(...getBaseRouteNamesRecursive(route.children));
        }
    });

    return [...new Set(names)]; // 去重
};

export function useMenu(): { menuList: Ref<MenuOptions[]> } {
    const accountStore = useAccountStore();
    const userStore = useUserStore();
    const processRoute = (route: RouteRecordRaw) => {
        // 处理 meta.title
        if (route.meta && typeof route.meta.title === "function") {
            route.meta.title = route.meta.title({ userStore });
        }
        // 递归处理子路由
        if (route.children && Array.isArray(route.children)) {
            route.children.forEach(childRoute => processRoute(childRoute));
        }
        return route;
    };
    const routeList = syncRouters.map(route => processRoute({ ...route }));
    baseRoleRouteName = [...baseRoleRouteName, ...getBaseRouteNamesRecursive(routeList)];
    // 菜单列表
    const menuList = ref<MenuOptions[]>([]);

    // 获取可见菜单
    const getVisibleMenuList = (routes: RouteRecordRaw[], level = 1): MenuOptions[] => {
        const personAuth = !!userStore.userInfo?.proxyAccount?.realName;
        // 组织机构认证
        const institutionAuth = !!userStore.userInfo?.proxyAccount?.applyState;
        const visibleMenus: MenuOptions[] = [];
        routes.forEach(route => {
            // 如果未完成个人认证或组织机构认证，只展示账号中心菜单
            if (!personAuth || !institutionAuth) {
                if (!baseRoleRouteName.includes(route.name as string)) {
                    return;
                }
            }

            // 判断是否隐藏
            const isHidden =
                typeof route.meta?.hidden === "function" ? route.meta.hidden({ accountStore, userStore }) : route.meta?.hidden;

            // 如果不隐藏且有标题，则添加到菜单
            if (isHidden || !route.meta?.title) {
                return;
            }

            const menuItem: MenuOptions = { ...route };
            menuItem.meta = { ...menuItem.meta, level };

            // 递归处理子菜单
            if (route.children?.length) {
                menuItem.children = getVisibleMenuList(route.children, level + 1);
            }

            visibleMenus.push(menuItem);
        });

        return visibleMenus;
    };

    menuList.value = getVisibleMenuList(routeList);

    watch(
        [() => accountStore, () => userStore],
        () => {
            // 账号变化后，刷新菜单
            menuList.value = getVisibleMenuList(routeList);
        },
        { deep: true }
    );
    return {
        menuList
    };
}
